import { ConnectError, Code, type Interceptor } from '@connectrpc/connect'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import { authStore } from '../stores/auth.store'
import { supabase } from './supabase'
import { createDemoTransport } from './demo-transport'
import { isDemoModeActive } from './demo-mode'
import { getErrorMessage, isExpectedError, readReason, reportError } from './error-reporting'
import { BILLING_SUSPENDED_PATH, ONBOARDING_PATH, isOnboardingPath } from './billing-gate'
import {
  newRequestId,
  recordResponseCorrelation,
} from './observability'

// When the backend billing interceptor denies a request (FailedPrecondition
// + one of the §6.4 reasons), send the user to the matching full-screen
// destination instead of leaving them on blank skeletons. Every billing RPC
// (and the three billing-gate destinations themselves) is ClassAlways, so
// each of these pages always loads. Guarded against loops: never navigate
// while already on the target, and debounce so a burst of failing queries
// triggers at most one navigation.
const BILLING_URL = '/company?section=subscription'
const SUBSCRIPTION_REDIRECT_DEBOUNCE_MS = 5000
let lastSubscriptionRedirectAt = 0

function redirectOnce(targetHref: string, alreadyThere: boolean) {
  if (alreadyThere) return
  const now = Date.now()
  if (now - lastSubscriptionRedirectAt < SUBSCRIPTION_REDIRECT_DEBOUNCE_MS) return
  lastSubscriptionRedirectAt = now
  window.location.href = targetHref
}

// Paid expiry is billing-only and recovers on the billing settings page.
function redirectToBillingPage() {
  const onBillingPage =
    window.location.pathname === '/company' &&
    new URLSearchParams(window.location.search).get('section') === 'subscription'
  redirectOnce(BILLING_URL, onBillingPage)
}

function redirectToOnboarding() {
  redirectOnce(ONBOARDING_PATH, isOnboardingPath(window.location.pathname))
}

// billing_suspended (an active R-83 suspend override): the F8 full-screen
// suspended screen. No checkout is offered there (DEC-19).
function redirectToBillingSuspended() {
  redirectOnce(BILLING_SUSPENDED_PATH, window.location.pathname === BILLING_SUSPENDED_PATH)
}

/**
 * Authentication and product-policy handling. Exported for direct unit
 * testing of the billing-gate reason branches (§6.4).
 */
export const authInterceptor: Interceptor = (next) => async (req) => {
  // Resolve the token through supabase.auth.getSession() rather than reading
  // authStore directly: getSession() transparently refreshes an expired token,
  // whereas the store can still hold a stale one. autoRefreshToken only runs on
  // an active tab, so a tab backgrounded (or a laptop asleep) past expiry wakes
  // up with an expired token in the store and every RPC on the next page fails
  // with "unauthenticated: invalid token". (Sentry CORTADO-FRONTEND-11)
  let accessToken = authStore.state.session?.access_token
  if (!isDemoModeActive()) {
    try {
      const { data } = await supabase.auth.getSession()
      accessToken = data.session?.access_token ?? accessToken
    } catch {
      // Keep the store's token as a fallback — a refresh failure should surface
      // as the Unauthenticated path below, not as a request with no auth header.
    }
  }
  if (accessToken) {
    req.header.set('Authorization', `Bearer ${accessToken}`)
  }

  try {
    return await next(req)
  } catch (error) {
    // Handle Unauthenticated errors by signing out
    if (error instanceof ConnectError && error.code === Code.Unauthenticated) {
      if (!isDemoModeActive()) {
        // Clear auth session and notify user
        await supabase.auth.signOut()
        toast.error('Session expired. Please log in again.')
      }
    }

    // Handle billing-gate denials (§6.4 error-reason contract).
    if (error instanceof ConnectError && error.code === Code.FailedPrecondition) {
      const reason = readReason(error)

      if (reason === 'onboarding_required') {
        if (!isDemoModeActive()) {
          redirectToOnboarding()
        }
      } else if (reason === 'subscription_required' || reason === 'billing_read_only') {
        // Keep the user on the current page. The persistent banner explains
        // subscription-required access; this toast gives immediate feedback
        // for the write that was refused.
        if (!isDemoModeActive()) {
          const copy = getErrorMessage(error)
          toast.warning(copy.title, { description: copy.description })
        }
      } else if (reason === 'subscription_expired') {
        if (!isDemoModeActive()) {
          redirectToBillingPage()
        }
      } else if (reason === 'billing_suspended') {
        if (!isDemoModeActive()) {
          redirectToBillingSuspended()
        }
      }
    }

    throw error
  }
}

/**
 * One explicit RPC span sits above Connect's fetch span and carries a stable
 * request ID. Exported for the short-lived login/callback transports too.
 */
export const sentryRpcInterceptor: Interceptor = (next) => async (req) => {
  const clientRequestId = newRequestId()
  req.header.set('X-Request-ID', clientRequestId)
  const rpcName = `${req.service.typeName}/${req.method.name}`

  Sentry.addBreadcrumb({
    category: 'rpc.client',
    message: rpcName,
    level: 'info',
    data: {
      service: req.service.typeName,
      method: req.method.name,
      requestId: clientRequestId,
    },
  })

  return Sentry.startSpan(
    {
      name: rpcName,
      op: 'rpc.client',
      attributes: {
        'rpc.system': 'connect',
        'rpc.service': req.service.typeName,
        'rpc.method': req.method.name,
        'http.request_id': clientRequestId,
      },
    },
    async (span) => {
      try {
        const response = await next(req)
        recordResponseCorrelation(response.header, span)
        span.setStatus({ code: 1 })
        return response
      } catch (error) {
        const connectErr = ConnectError.from(error)
        const backendRequestId =
          connectErr.metadata?.get('X-Request-ID') || clientRequestId
        const backendTraceId = connectErr.metadata?.get('X-Trace-ID')
        span.setAttribute('server.request_id', backendRequestId)
        if (backendTraceId) {
          span.setAttribute('sentry.trace_id', backendTraceId)
        }
        span.setAttribute('rpc.connect_rpc.error_code', connectErr.code)
        span.setStatus({
          code: 2,
          message:
            connectErr.code === Code.Canceled ? 'cancelled' : 'internal_error',
        })

        const isNetworkError =
          connectErr.code === Code.Unknown &&
          /Failed to fetch|NetworkError|Load failed/i.test(connectErr.rawMessage)

        if (!isNetworkError && !isExpectedError(connectErr)) {
          reportError(error, {
            feature: 'api',
            apiService: req.service.typeName,
            apiMethod: req.method.name,
            backendRequestId,
            backendTraceId,
          })
        }
        throw error
      }
    }
  )
}

/**
 * Create Connect transport for communicating with the backend API
 *
 * When demo mode is active (VITE_DEMO_MODE_ENABLED=true && ?demo=true),
 * the transport will intercept API calls and return mock data.
 */
export const connectTransport = createDemoTransport({
  baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:8080',
  // Use binary protobuf instead of the default Connect JSON codec. List
  // endpoints return large InvoiceLite/InquiryLite/EventLite payloads; binary
  // is markedly smaller on the wire and faster to parse than JSON. The Go
  // backend negotiates the codec per-request from Content-Type, so no server
  // change is needed. Demo mode short-circuits before serialization and the
  // interceptors only touch headers, so both are unaffected.
  useBinaryFormat: true,
  interceptors: [authInterceptor, sentryRpcInterceptor],
})

// Log demo mode status on initialization
if (isDemoModeActive()) {
  console.log(
    '%c Demo Mode Active ',
    'background: #8b5cf6; color: white; font-weight: bold; padding: 4px 8px; border-radius: 4px;'
  )
  console.log('API calls will return mock data. Add ?demo=true to any URL to stay in demo mode.')
}
